import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";

export type CmsAccess = {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  allowedTabs: Set<string>;
  hasTab: (key: string) => boolean;
};

const EMPTY: CmsAccess = {
  isAuthenticated: false,
  isAdmin: false,
  isEditor: false,
  allowedTabs: new Set(),
  hasTab: () => false,
};

async function fetchCmsAccess(): Promise<CmsAccess> {
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return EMPTY;
  const [{ data: roles }, { data: tabs }] = await Promise.all([
    (supabase as any).from("user_roles").select("role").eq("user_id", session.user.id),
    (supabase as any).from("user_tab_permissions").select("tab").eq("user_id", session.user.id),
  ]);
  const roleList = (roles as Array<{ role: string }> | null) ?? [];
  const isAdmin = roleList.some((r) => r.role === "admin");
  const isEditor = roleList.some((r) => r.role === "editor");
  const allowedTabs = new Set<string>((tabs as Array<{ tab: string }> | null)?.map((t) => t.tab) ?? []);
  return {
    isAuthenticated: true,
    isAdmin,
    isEditor,
    allowedTabs,
    hasTab: (key: string) => isAdmin || allowedTabs.has(key),
  };
}

export function useCmsAccess() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["cms-access"],
    queryFn: fetchCmsAccess,
    staleTime: 60_000,
  });

  // Berechtigungen bei Sign-In/Sign-Out neu laden, damit nach dem MFA-Login
  // nicht kurzzeitig „Keine Berechtigung" angezeigt wird.
  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    getSupabase().then((supabase) => {
      if (!active) return;
      const { data: sub } = supabase.auth.onAuthStateChange((event) => {
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "SIGNED_OUT") {
          qc.invalidateQueries({ queryKey: ["cms-access"] });
        }
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [qc]);

  return {
    access: query.data ?? EMPTY,
    loading: query.isLoading || query.isFetching,
  };
}
